import { Injectable, ApplicationRef, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Subject } from 'rxjs/Subject';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Cookie } from 'ng2-cookies/ng2-cookies';
import { environment } from '../../environments/environment';
import { LoggedInUser } from '../classes/logged-in-user';
import { User } from '../classes/user';
import { BaseService } from './base.service';
import { PopupService } from './popup.service';
import { MapService } from './map.service';
import { FeatureService } from "./feature.service";

@Injectable()
export class UserService extends BaseService {

  loggedIn: boolean = false;
  loggedInUser = new LoggedInUser();
  redirectUrl: string;
  notLoggedInUser = new User();
  rememberMeCookie: boolean = false;
  userInitials: string;
  user = new User();
  onSignUp: boolean = false;
  onSignIn: boolean = false;
  viewedSharedMap: any = null;
  userActiveWhenLoaded : boolean = false;
  organizationName : string;
  featureExecutionObject: any;
  isMobileUserOptionsOpened: boolean = false;

  mapService: any;

  linkedInFeatureSource = new BehaviorSubject<boolean>(false);
  linkedInFeatureStream = this.linkedInFeatureSource.asObservable();

  isLoggedInChangeSource = new BehaviorSubject<any>(null);
  isLoggedInChangeStream = this.isLoggedInChangeSource.asObservable();

  constructor(public httpClient: HttpClient, public router: Router, public appRef: ApplicationRef,
    public injector: Injector, public popupService: PopupService, public featureService: FeatureService,) {
    super();
    this.rememberMeCookie = Cookie.get('propertycapsule-' + environment.name + '.rememberMe') ? true : false;
    this.loggedIn = this.rememberMeCookie || !!Cookie.get('propertycapsule-' + environment.name + '.loggedIn');
    this.isLoggedInChangeSource.next(this.loggedIn);

    this.injectMapService();
  }

  injectMapService() {
    if(!this.mapService) {
      this.mapService = this.injector.get(MapService);
    }
  }

  setEmailAddress(email: string) {
    this.notLoggedInUser.emailAddress = email;
  }

  getEmailAddress(): string {
    return this.notLoggedInUser.emailAddress;
  }

  requiresDomainCookieRedirect() {
    return this.isJLLSite() && this.isSafari();
  }

  getLoginRedirectPath() {
    let w = (<any>window),
    redirectUrl = w.location.href;
    redirectUrl = redirectUrl.replace('/log-in', '');
    redirectUrl = redirectUrl.replace('/sign-up', '');
    redirectUrl = redirectUrl.replace('open', 'map');
    return redirectUrl;
  }

  getUser(user) {
    return this.httpClient.get(
        environment.APP.API_URL + '/user/' + (user.id || user.userId)
      )
      .map((res) => {
        return res;
      });
  }

  updateUser(user) {
    const headers = { 'Content-Type': 'application/json' };

    return this.httpClient.put(
        environment.APP.API_URL + '/user/' + (user.id || user.userId),
        JSON.stringify(user),
        { headers }
      )
      .map((res) => {
        return res;
      });
  }

  getLoggedInUser() {
    return this.httpClient.get(
        environment.APP.API_URL + '/user-token'
      )
      .map((res: any) => {
        this.loggedInUser = res;
        return res;
      });
  }

  onLogin(res?) {
    this.loggedIn = true;
    this.setLoggedInCookie();
    this.clearLS();
    if(res && res.redirectUrl) {
      window.location.replace(res.redirectUrl);
    } else {
      this.isLoggedInChangeSource.next(this.loggedIn);
      return this.loggedIn;
    }
  }

  forgotPassword(user) {
    const headers = { 'Content-Type': 'application/json' };

    return this.httpClient.post(
      environment.APP.AUTH_URL + '/password-request?source=maps',
      JSON.stringify(user),
      { headers }
    )
    .map((res) => {
      return res;
    })
  }

  resetPassword(user, requestId: string, isLoggedInUserChangePassword: boolean) {
    const headers = { 'Content-Type': 'application/json' };

    let url = '';
    if(isLoggedInUserChangePassword) {
      url = environment.APP.AUTH_URL + '/password-change?source=maps';
    } else {
      url = environment.APP.AUTH_URL + '/password-request/' + requestId + '?source=maps';
    }

    return this.httpClient.put(
        url,
        JSON.stringify(user),
        { headers }
      )
      .map((res) => {
        return res;
      });
  }

  setLoggedInCookie(rememberMe?: boolean) {
    if(rememberMe) {
      Cookie.set('propertycapsule-' + environment.name + '.rememberMe', 'true', 14, '/', this.getDomainName());
    } else {
      Cookie.set('propertycapsule-' + environment.name + '.loggedIn', 'true', 0.167, '/', this.getDomainName()); // 4 hours
    }
  }

  logout() {
    return this.httpClient.get(
      environment.APP.AUTH_URL + '/logout?source=maps'
    )
    .map((res) => this.onLogout());
  }

  clearLS() {
    let w = (<any>window);
    if(w.lsAvailable) {
      w.localStorage.removeItem('propertyCapsule-' + environment.name + '-mapData');
      w.localStorage.removeItem('propertyCapsule-' + environment.name + '-demographicData');
      w.localStorage.removeItem('propertyCapsule-' + environment.name + '-mapAccessId');
    }
  }

  onLogout() {
    let w = (<any>window);
    this.isAdminModeActive() && w.mmap.toggleAdminMode(false);
    this.loggedIn = false;
    this.resetSegment();
    Cookie.delete('propertycapsule-' + environment.name + '.loggedIn', '/', this.getDomainName());
    Cookie.delete('propertycapsule-' + environment.name + '.rememberMe', '/', this.getDomainName());
    w.Events.publish('closeUserMap');
    this.clearLS();
    this.clearUserData();
    this.isLoggedInChangeSource.next(this.loggedIn);
    this.featureService.updateFeature();
  }

  clearUserData() {
    let w = (<any>window);
    this.user = new User();
    this.onSignUp = false;
    this.onSignIn = false;
    this.loggedInUser = new LoggedInUser();
    w.Events.publish('resetMyStuffData');
  }

  getLoggedIn() {
    return this.loggedIn;
  }

  getUserProfilePictureUrl() {
    return environment.APP.API_URL + '/user/photo/download?id=' + this.loggedInUser.photoMediaId;
  }

  updateUserProfilePic(data) {
    let file:File = data,
        formData:FormData = new FormData();
    formData.append('file', file, file.name);

    const headers = { 'Accept': 'application/json' };

    return this.httpClient.post(
      environment.APP.API_URL + '/user/photo-upload',
      formData,
      { headers }
    )
    .map((res) => {
      return res;
    });
  }

  onSingleMapClick(map, isReceived?, keepPopupOpen?) {
    let w = (<any>window);
    this.injectMapService();

    this.mapService.checkIfMapIsReceived(map.profileId !== this.loggedInUser.profileId);
    if(!this.mapService.currentUserMap) {
      w.saveMap.saveLastUnsavedMap();
    }
    w.Events.publish('initializeBrandingMode', map && map.brandingInfo? map.brandingInfo: null);
    w.saveMap.load(map, null, this.mapService.isMapReceived);
    this.mapService.currentUserMap = map;
    w.notifyChange = this.mapService.currentUserMap ? true : false;
    this.mapService.currentUserMap.canRestore = false;
    this.mapService.shareMapChangeSource.next(true);

    !keepPopupOpen && this.popupService.updateRoute();
    w.resizeMapNameIfLong();
    this.appRef.tick();

    setTimeout(()=>{
      this.router.navigate([], {
        queryParams: {
          m: map.accessId
        },
        replaceUrl: true
      });
    }, 0);
  }

  restoreCurrentUserMap() {
    this.mapService.currentUserMap && this.onSingleMapClick(this.mapService.currentUserMap);
  }

  resetSegment() {
    let w = (<any>window);
    if (environment.APP.SEGMENT_TRACKING && w.mmap.isIntegratedMapsActive()) {
      w.analytics && w.analytics.reset();
      w.mixpanel && w.mixpanel.reset();
    }
  }

  isAdminModeActive() {
    let w = (<any>window);
    return w.mmap? this.loggedIn && this.loggedInUser.systemAdmin && w.mmap.adminMode: false;
  }

  isOrganizationEditAdmin() {
    let w = (<any>window);
    return this.loggedIn && w.mmap.masterMap && w.mmap.masterEdit && this.loggedInUser.organizationAdmin;
  }

  isMasterMapModeActive() {
    let w = (<any>window);
    return w.mmap.masterMap;
  }

  getOrganizations() {
    return this.httpClient.get(
      environment.APP.API_URL + '/organization'
    )
    .map((res) => {
      return res;
    });
  }

  getDomainName() {
    if(this.isJLLSite()) {
      return environment.APP.JLL.HOSTNAME;
    } else {
      return environment.APP.DOMAIN;
    }
  }

  isJLLSite() {
    return (<any>window).location.hostname === environment.APP.JLL.HOSTNAME;
  }

  isSafari() {
    let isSafari = !!navigator.userAgent.match(/Version\/[\d\.]+.*Safari/);
    return isSafari;
  }

  openMobileUserOptions() {
    this.isMobileUserOptionsOpened = !this.isMobileUserOptionsOpened;
    (<any>window).Events.publish('detectChanges');
  }

  verifyUserEmail(params) {
    const requestOptions = {
      params: params
    };

    return this.httpClient.get(
      environment.APP.AUTH_URL +  '/user/verify',
      requestOptions
    )
    .map((res:any) => {
      return res;
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/services/user.service.ts